import { React, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/AddDevice.css';
import CustomButton from '../CustomElements/CustomButton';
import CustomContainer from '../CustomElements/CustomContainer';
import CustomTitle from '../CustomElements/CustomTitle';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import Input from '../CustomElements/Input';
import FormContainer from '../CustomElements/FormContainer';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTextarea from '../CustomElements/CustomTextarea';
import PhoneInput from '../CustomElements/PhoneInput';
import FilePicker from '../FileManager/FilePicker';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
// Утилиты
import { handleUniversalChange } from '../../utils/formUtils';
import { isValidDeviceID } from '../../utils/isValidDeviceID';
import { isValidAndroidID } from '../../utils/isValidAndroidID';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings';
import { omitFields } from '../../utils/omitFields';
import { convertStringValuesToNumbers } from '../../utils/convertStringValuesToNumbers';
import { transformFieldsToMoscowStart } from '../../utils/convertToDate';
// Действия Redux
import { newDeviceFromServer } from '../../redux/actions/devices.action';
import { load, remove } from '../../redux/actions/loader.action';
import { addError } from '../../redux/actions//error.action';
import { initIurAndroidIdFromServer } from '../../redux/actions/devices.action';
import { initFilesFromServer } from '../../redux/actions/files.action';

// Этот компонент отображает форму для добавления нового устройства
// и обрабатывает отправку данных на сервер.

function AddDevice({ closeModal, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // Получаем опции
  const optionsForSelect = useSelector((state) => state.options);

  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);

  // состояние для отображения страницы подтверждения
  const [addSuccess, setAddSuccess] = useState(false);

  // состояние для необязательных полей
  const [optionalFields, setOptionalFields] = useState([
    'isBroken',
    'note',
    'storageId',
    'receiverId',
    'sim1',
    'sim2',
    'senderId',
  ]);

  // Состояние для формы
  const [formData, setFormData] = useState({
    id: '',
    receiveDate: '',
    storageId: '',
    receiverId: '',
    sim1: '',
    isBroken: '',
    typeId: '',
    acceptanceCert: null,
    senderId: '',
    manufactureDate: '',
    sim2: '',
    note: '',
    androidId: '',
  });

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    // Проверяем валидность id
    if (!isValidDeviceID(formData.id, formData.typeId)) {
      return dispatch(addError({ error: t?.invalidDeviceIdError || 'Указан невалидный ID устройства' }));
    }
    // Проверяем валидность androidId, если он предоставлен
    if (formData?.androidId && !isValidAndroidID(formData.androidId)) {
      return dispatch(addError({ error: t?.invalidAndroidIdError || 'Указан невалидный ANDROID_ID' }));
    }
    // Если все проверки пройдены успешно, переходим к отправке данных
    // Переводим в состояние загрузки
    dispatch(load());
    // Удаляем пустые строки
    const formWithoutEmptyStrings = removeEmptyStrings(formData);
    // Убираем ненужные поля из формы
    const formWithoutExcluded = omitFields(formWithoutEmptyStrings, ['acceptanceCertName']);
    // Преобразуем дату (если есть) в начало дня по Москве
    const formWithMoscowDate = transformFieldsToMoscowStart(formWithoutExcluded, ['manufactureDate', 'receiveDate']);
    // Преобразуем нужные строковые поля в числа
    const payload = convertStringValuesToNumbers(formWithMoscowDate, ['id']);
    // Отправляем данные на сервер через Redux
    dispatch(newDeviceFromServer(payload));
  };

  // если выбрали тип иур добавляем обязательное поле androidId
  useEffect(() => {
    // Удалить 'androidId', если typeId !== 1
    if (formData.typeId === 1) {
      setOptionalFields((prevFields) => prevFields?.filter((field) => field !== 'androidId'));
      // Добавить 'androidId', если typeId === 1
    } else if (formData.typeId !== 1 && formData.typeId !== '') {
      setOptionalFields((prevFields) => {
        // Проверить, существует ли уже 'androidId' в массиве, чтобы избежать дублирования
        if (!prevFields.includes('androidId')) {
          return [...prevFields, 'androidId'];
        }
        return prevFields; // Возвращаем неизмененный массив, если 'androidId' уже есть
      });
    }
  }, [formData]);

  //отслеживаем выполнение запроса
  useEffect(() => {
    // Если нет ошибок, закрываем модальное окно
    if (!loader?.isLoading && loader?.isSuccess) {
      setAddSuccess(true);
    }
    // при закрытии компонента чистим стейт загрузки
    return () => {
      dispatch(remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess]);

  // Закрывает модальное окно после успешного добавления нового устройства
  const handlerCloseModal = () => {
    closeModal();
    setAddSuccess(false);
  };

  // при сбросе даты производства  сбрасываем дату приемки
  useEffect(() => {
    setFormData((prev) => ({ ...prev, receiveDate: '' }));
  }, [formData.manufactureDate]);

  // отправляем запрос на получений списка androidId при открытии формы
  useEffect(() => {
    dispatch(initIurAndroidIdFromServer());
    dispatch(initFilesFromServer());
  }, [dispatch]);

  return (
    <CustomContainer>
      <CustomTitle titleText={t?.addDeviceTitle || 'ДОБАВЛЕНИЕ НОВОГО УСТРОЙСТВА'} />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.successAddDevice || 'Новое устройство добавлено!'}
          onConfirm={handlerCloseModal}
          addNew={t?.backToList || 'К списку устройств'}
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <Input
            column="left"
            label={`${t?.labelDeviceId} *` || 'ID устройства *'}
            placeholder={t?.placeholderDeviceId || 'Введите ID устройства'}
            onChange={(event) => handleUniversalChange(formData, setFormData, event, null, null, null, null, null, 6)}
            name="id"
            width="100%"
            height="var(--sadr-font-size-60)"
            maxLength={6}
          />
          <DateSelector
            column="left"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={t?.labelManufactureDate || 'Дата производства *'}
            placeholder={t?.placeholderManufactureDate || 'Выберите дату производства'}
            selectedDate={formData.manufactureDate}
            setSelectedDate={(date) =>
              handleUniversalChange(formData, setFormData, null, 'manufactureDate', date, 'date')
            }
            name="manufactureDate"
            maxDate={new Date()}
          />

          <Selector
            column="left"
            options={optionsForSelect.storages}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'storageId', selectedOption, 'select');
            }}
            placeholder={t?.placeholderStorageLocation || 'Выберите место хранения'}
            label={t?.storageLocationTitle || 'Место хранения'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="storageId"
          />
          <Selector
            column="left"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'senderId', selectedOption, 'select')
            }
            placeholder={t?.senderNamePlaceholder || 'ФИО передавшего'}
            label={t?.labelSenderName || 'ФИО передавшего *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="senderId"
          />
          <PhoneInput
            column="left"
            label="SIM1"
            name="sim1"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            value={formData.sim1}
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
            selfAlign="start"
          />
          {formData.typeId === 1 && (
            <Selector
              column="left"
              options={optionsForSelect?.iurAndroidId}
              onChange={(selectedOption) =>
                handleUniversalChange(formData, setFormData, null, 'androidId', selectedOption, 'select')
              }
              placeholder={t?.placeholderSelectAndroidId || 'Выберите android_id'}
              label={t?.labelAndroidId || 'ANDROID_ID *'}
              width="100%"
              height="var(--sadr-font-size-60)"
              isMulti={false}
              name="androidId"
              selfAlign="start"
              optionText={t?.textNoUnauthorizedIur || 'Нет неавторизованных ИУР'}
            />
          )}
          <Selector
            column="right"
            options={optionsForSelect?.deviceTypes?.filter((item) => item.value !== 4 && item.value !== 2)}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'typeId', selectedOption, 'select')
            }
            placeholder={t?.placeholderDeviceType || 'Выберите тип устройства'}
            label={`${t?.labelDeviceType} *` || 'Тип устройства *'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="typeId"
          />
          <DateSelector
            column="right"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={`${t?.labelReceiveDate} *` || 'Дата приемки *'}
            placeholder={t?.selectAcceptanceDate || 'Выберите дату приемки'}
            selectedDate={formData.receiveDate}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'receiveDate', date, 'date')}
            name="receiveDate"
            minDate={formData?.manufactureDate}
            maxDate={new Date()}
          />
          <Selector
            column="right"
            options={optionsForSelect?.condition}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'isBroken', selectedOption, 'select')
            }
            label={t?.deviceConditionTitle || 'Состояние'}
            placeholder={t?.placeholderCondition || 'Не определено'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="isBroken"
          />
          <Selector
            column="right"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'receiverId', selectedOption, 'select')
            }
            label={t?.receivedByTitle || 'ФИО принявшего'}
            placeholder={t?.placeholderReceiverName || 'Выберите ФИО принявшего'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="receiverId"
          />
          <PhoneInput
            column="right"
            label="SIM2"
            name="sim2"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            value={formData.sim2}
          />
          <CustomTextarea
            column="right"
            label={t?.commentTitle || 'Комментарий'}
            placeholder={t?.placeholderComment || 'Оставьте комментарий'}
            type="textArea"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="note"
            rowSpan={2}
            width="100%"
            height="var(--sadr-font-size-200)"
          />

          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            margin={'var(--sadr-font-size-20)0 0 0'}
            text={t?.addDeviceButton || 'Добавить устройство'}
            onClick={handlerSubmit}
            disabled={isButtonDisabled}
            isButton
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default AddDevice;
